import type { OnChainVenue, Quote, Venue } from './types'

/**
 * The numbers the user is shown, and the order the routes appear in. Pure on
 * purpose: this is where a wrong sign or a rounding in the wrong direction
 * becomes a person receiving less than the screen promised, and pure functions
 * are the only part of a swap that can be proven without a chain.
 */

/**
 * Tie-break order when two routes return exactly the same amount. It is a
 * preference, not a judgement about quality: the deepest venues first, so an
 * identical quote resolves toward the pool most likely to still be there when
 * the transaction lands.
 */
const VENUE_ORDER: Venue[] = ['aero-v2', 'uni-v3', 'aero-cl', 'aero-cl3', 'uni-v2', 'kyber']

function venueRank(venue: Venue): number {
  const i = VENUE_ORDER.indexOf(venue)
  return i === -1 ? VENUE_ORDER.length : i
}

function poolCount(quote: Quote): number {
  return 'pools' in quote.candidate ? quote.candidate.pools.length : 1
}

/** Most received first. Never mutates the input. */
export function rankQuotes(quotes: Quote[]): Quote[] {
  return [...quotes].sort((a, b) => {
    if (a.amountOut !== b.amountOut) return a.amountOut > b.amountOut ? -1 : 1
    const pools = poolCount(a) - poolCount(b)
    if (pools !== 0) return pools
    return venueRank(a.candidate.venue) - venueRank(b.candidate.venue)
  })
}

/**
 * How much worse the price got because of the size of this trade.
 *
 * Measured, not modelled: the reference quote is the same route asked for a
 * thousandth of the amount, which is the price a trade too small to move the
 * pool would get. The ratio between the two rates is the impact, and it is
 * correct for a concentrated pool as well as a constant-product one — a
 * formula built on reserves is not.
 */
export function priceImpactBps(
  amountIn: bigint,
  amountOut: bigint,
  refIn: bigint,
  refOut: bigint
): number | undefined {
  if (refOut === 0n || refIn === 0n || amountIn === 0n) return undefined
  const achieved = amountOut * refIn * 10000n
  const reference = amountIn * refOut
  const ratioBps = achieved / reference
  const impact = 10000n - ratioBps
  if (impact <= 0n) return 0
  if (impact >= 10000n) return 10000
  return Number(impact)
}

/**
 * The floor written into the transaction. Integer division floors, which is
 * the direction that protects the user: a minimum rounded up is a minimum the
 * pool may be unable to meet.
 */
export function minAmountOut(amountOut: bigint, slippageBps: number): bigint {
  const bps = BigInt(Math.max(0, Math.min(10000, Math.round(slippageBps))))
  return (amountOut * (10000n - bps)) / 10000n
}

/** Computed at send time, never at quote time — a deadline is not a quote. */
export function deadlineFromNow(minutes: number, nowMs: number = Date.now()): bigint {
  return BigInt(Math.floor(nowMs / 1000) + Math.round(minutes) * 60)
}

/**
 * How far below the best a quote is, in percent, for the route list. Positive
 * numbers never appear: the best route is the reference, so every other row is
 * zero or worse, and the sign says so.
 */
export function relativeDeltaPercent(quote: Quote, best: Quote): number {
  if (best.amountOut === 0n) return 0
  const diff = quote.amountOut - best.amountOut
  const scaled = (diff * 1000000n) / best.amountOut
  return Number(scaled) / 10000
}

export function venueLabel(venue: Venue): string {
  switch (venue) {
    case 'aero-v2':
      return 'Aerodrome'
    case 'aero-cl':
      return 'Aerodrome Slipstream'
    /*
     * Both Slipstream deployments carry the venue's name; the factory is the
     * pool's property, so `poolLabel` is where it shows. Two rows reading
     * "Aerodrome Slipstream · f3 ts 10" and "· ts 100" tell a reader the same
     * thing the fee tiers tell them under Uniswap.
     */
    case 'aero-cl3':
      return 'Aerodrome Slipstream'
    case 'uni-v3':
      return 'Uniswap v3'
    case 'uni-v2':
      return 'Uniswap v2'
    case 'kyber':
      return 'KyberSwap'
    default:
      return venue
  }
}

/** "Uniswap v3 0.3%", "Aerodrome stable", "Aerodrome Slipstream f3 ts 10". */
export function poolLabel(venue: OnChainVenue, pool: { stable?: boolean; fee?: number; tickSpacing?: number }): string {
  switch (venue) {
    case 'aero-v2':
      return pool.stable ? 'stable' : 'volatile'
    case 'uni-v3':
      return pool.fee === undefined ? '' : `${pool.fee / 10000}%`
    case 'aero-cl':
      return pool.tickSpacing === undefined ? '' : `ts ${pool.tickSpacing}`
    case 'aero-cl3':
      return pool.tickSpacing === undefined ? 'f3' : `f3 ts ${pool.tickSpacing}`
    case 'uni-v2':
      return ''
    default:
      return ''
  }
}
